// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React, { useEffect, useState } from "react";

import { Button, Grid, Menu, MenuItem, styled } from "@3778.care/goban";
import EditIcon from "@mui/icons-material/Edit";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { useMixpanel } from "../../../../contexts/mixpanel";
import { dayInPast, isWithinRange } from "../../../../util/date";
import { useIsDesktop } from "../../../../util/useIsDesktop";
import { CardButton } from "../CardButton";

const StyledButton = styled(Button)(({ theme: { palette, spacing } }) => ({
  paddingLeft: spacing(6.25),
  paddingRight: spacing(6.25),
  backgroundColor: palette.grey[300],
  "&:hover": {
    backgroundColor: palette.grey[500],
  },
}));

const StyledIcon = styled(EditIcon)(({ theme: { palette } }) => ({
  color: palette.text.secondary,
}));

const ONE_MINUTE = 60000;

export const AppointmentActions = ({
  appointment,
  appointmentLocation,
  appointmentType,
}) => {
  const isDesktop = useIsDesktop();

  const [anchorEl, setAnchorEl] = useState(null);
  const [inRange, setInRange] = useState(isWithinRange(appointment.start));

  const buttonSize = isDesktop ? "large" : "small";
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { track } = useMixpanel();

  useEffect(() => {
    const interval = setInterval(() => {
      setInRange(isWithinRange(appointment.start));
    }, ONE_MINUTE);

    return () => clearInterval(interval);
  }, [appointment.start]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (action) => () => {
    setAnchorEl(null);
    if (action) {
      track(`Appointment - ${action}`);
      navigate(`/appointment/${appointment.id}/${action}`);
    }
  };

  return (
    <>
      {!dayInPast(appointment.start) && (
        <Grid container>
          <Grid item xs={4}>
            <StyledButton
              aria-controls="fade-menu"
              aria-haspopup="true"
              data-cy="calendly-actions"
              disableElevation
              fullWidth={true}
              onClick={handleClick}
              size={buttonSize}
              startIcon={<StyledIcon />}
              variant="contained"
            >
              Alterar
            </StyledButton>

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              id="fade-menu"
              keepMounted
              onClose={handleClose()}
              open={open}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <MenuItem
                color="textPrimary"
                data-cy="appointment-action"
                onClick={handleClose("reschedule")}
                variant="body1"
              >
                Reagendar
              </MenuItem>

              <MenuItem
                color="textPrimary"
                data-cy="appointment-action"
                onClick={handleClose("cancel")}
                variant="body1"
              >
                Cancelar
              </MenuItem>
            </Menu>
          </Grid>

          <Grid item xs={8}>
            <CardButton
              appointment={appointment}
              appointmentInRange={inRange}
              appointmentType={appointmentType}
              appointmentLocation={appointmentLocation}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

AppointmentActions.propTypes = {
  appointment: PropTypes.object.isRequired,
  appointmentLocation: PropTypes.string,
  appointmentType: PropTypes.string.isRequired,
};
